import {isArray, isString, isUndefined, noop} from 'lodash';
import React, {memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Subject} from 'rxjs';
import styled from 'styled-components';

import {readApiModel} from '../../../libs/shared/core/src/helpers/api/apiHelpers';
import {findGlobalOrigin} from '../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {isPreview} from '../../../libs/shared/core/src/helpers/environment/envHelpers';
import {annotateLegacyFrameVisibilityChange} from '../../../libs/shared/core/src/helpers/instrumentation/eventLoopPerformanceHelpers';
import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {
  useBuildCancelToken,
  usePrevious,
} from '../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {ErrorWithData} from '../../../libs/shared/core/src/helpers/types/customErrors';
import {isDefined, isPromise} from '../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {CancelError} from '../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {HttpNetworkError} from '../../../libs/shared/core/src/services/http/httpError';
import {logger} from '../../../libs/shared/core/src/services/logger/logger';
import {usePush, usePushEvents} from '../../../libs/shared/core/src/services/push/pushContext';
import {getHasSettingsNavigationV2} from '../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {AppPanelId, makeSettingsPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {AppFrame} from '../../../libs/shared/ui/src/components/appFrame/appFrame';
import {Iframe} from '../../../libs/shared/ui/src/components/iframe/iframe';
import {Loader} from '../../../libs/shared/ui/src/components/loaders/loader';
import {useLinkNavigation} from '../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {center} from '../../../libs/shared/ui/src/styles/mixins';
import {DarkSidebarThemeProvider} from '../../../libs/shared/ui/src/theme/darkSidebarThemeProvider';
import {ExpandedDarkThemeProvider} from '../../../libs/shared/ui/src/theme/expandedDarkThemeProvider';
import {shouldRelayPushEventToLegacyFrame} from '../../../srcCommon/push/pushHelpers';
import {fetchRawBootMaybeIncrementallyAsync} from '../../../srcCommon/services/boot/bootOnline';
import {useAppDispatch, useAppSelector} from '../../helpers/appReduxHelpers';
import {isLegacyPath} from '../../helpers/legacyHelpers';
import {SingleUseValue} from '../../helpers/memoryHelpers';
import {
  ApiLegacyFrameMethods,
  LegacyFrameMethodsEnum,
  LegacyFrameMethodsModel,
} from '../../models/frames/legacyFrameMethodsModel';
import {SettingsBannersHost} from '../../settings/banners/settingsBannersHost';
import {SettingsBannersProvider} from '../../settings/banners/settingsBannersProvider';
import {SettingsNavigation} from '../../settings/navigationV2/settingsNavigation';
import {SettingsNavigationProvider} from '../../settings/navigationV2/settingsNavigationProvider';
import {ThunkAction, ThunkAsyncAction} from '../../store/appThunk';
import {LegacyAppWindow} from './legacyApp';
import {
  composeLegacyFrameAction,
  navigateLegacyFrameAction,
  openUrlLegacyFrameAction,
  resetOfflineCacheLegacyFrameAction,
} from './legacyFrameActions';
import {useLegacyFrameContext} from './legacyFrameContext';
import {
  fetchLegacyFrameDeltaBootAsync,
  LegacyFrameRawBoot,
  toRawBootForLegacyFrame,
} from './legacyFrameHelpers';

/*
 * Constants.
 */

interface CallLegacyMessage {
  type: string;
  uid?: string;
  method: string;
  args: ReadonlyArray<unknown>;
}

interface LegacyNavigationTiming {
  path: string;
  start: number;
}

const messageTypeProxy = 'proxy-call';

/*
 * Props.
 */

interface LegacyFrameProps {
  panelId: AppPanelId;
  rawBoot$: Subject<LegacyFrameRawBoot>;
  isVisible: boolean;
  legacyPath: string | undefined;
  companyUrl: string;
  initialRawBoot?: SingleUseValue<unknown>;
}

/*
 * Style.
 */

const StyledAppFrame = styled(AppFrame)`
  display: ${(p) => (p.isVisible ? 'grid' : 'none')};
  grid-template-areas:
    'menu topbar'
    'menu main';
  grid-template-columns: auto 1fr;
  grid-template-rows: auto 1fr;
`;

const StyledAppFrameV2 = styled(AppFrame)`
  display: ${(p) => (p.isVisible ? 'grid' : 'none')};
  grid-template-areas:
    'switcher menu topbar'
    'switcher menu main';
  grid-template-columns: auto auto 1fr;
  grid-template-rows: auto 1fr;
`;

const StyledMainDiv = styled.div`
  grid-area: main;
  position: relative;
  overflow: hidden;
`;

const StyledCenteredDiv = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  ${center()};
`;

/*
 * Logger.
 */

const legacyFrameLogger = logger.getLogger('legacy-frame');

/*
 * Component.
 */

export const LegacyFrame = memo<LegacyFrameProps>((props) => {
  const dispatch = useAppDispatch();
  const {panelId, rawBoot$, isVisible, legacyPath, companyUrl, initialRawBoot} = props;
  const settingsPanelId = makeSettingsPanelId(panelId);
  const {navigateToLink} = useLinkNavigation();
  const {findNewLegacyRoute, findRewriteLegacyRoute} = useLegacyFrameContext();
  const hasSettingsNavigationV2 = useAppSelector(getHasSettingsNavigationV2);

  // The Old Front path or the Settings path to be rendered, if any.
  const visibleLegacyPath = isVisible ? legacyPath : undefined;
  const settingsPath = visibleLegacyPath?.startsWith('/settings') ? visibleLegacyPath : undefined;

  // The route to be served by New Front. In practice, this is a Settings route that has been migrated to New Front.
  const newLegacyRoute = useMemo(
    () => findNewLegacyRoute(visibleLegacyPath),
    [findNewLegacyRoute, visibleLegacyPath],
  );

  const isPartialRender = Boolean(newLegacyRoute?.route.partialRender);
  // The path to be served by Old Front.
  const isOldFrontVisible = isDefined(visibleLegacyPath) && (!newLegacyRoute || isPartialRender);
  const oldLegacyPath = isOldFrontVisible ? visibleLegacyPath : '/noop';

  const [isBooted, setIsBooted] = useState(false);
  const [hasOverlay, setHasOverlay] = useState(false);

  const frameWindowRef = useRef<LegacyAppWindow>();
  const isNavigatingLegacyAppRef = useRef(false);
  const isLoadedRef = useRef(false);
  const hasGoneOnlineRef = useRef<boolean>(false);
  const hasNavigatedToOldFrontRef = useRef<boolean>(false);
  const navigationTimingRef = useRef<LegacyNavigationTiming>();
  const origin = useMemo(() => findGlobalOrigin(), []);

  const {isConnected} = usePush();

  const {buildCancelToken} = useBuildCancelToken();

  // Load the frame only when online.
  if (isConnected) {
    hasGoneOnlineRef.current = true;
  }

  // Load the frame only after navigating to a patch served by Old Front.
  if (isOldFrontVisible) {
    hasNavigatedToOldFrontRef.current = true;
  }

  // Check if we should route the user to a new path.
  const legacyPathReRouted = useMemo(
    () => findRewriteLegacyRoute(visibleLegacyPath),
    [findRewriteLegacyRoute, visibleLegacyPath],
  );
  const previousLegacyPathReRouted = usePrevious(legacyPathReRouted);

  useEffect(() => {
    // If we do not have a re-routed path or we already tried to re-route the user.
    if (!legacyPathReRouted || legacyPathReRouted === previousLegacyPathReRouted) {
      return;
    }
    navigateToLink(legacyPathReRouted);
  }, [legacyPathReRouted, navigateToLink, previousLegacyPathReRouted]);

  /*
   * Send updates to the frame.
   */

  const callIframeMethod = useCallback(
    (method: string, ...args: Array<unknown>) => {
      const currentFrameWindow = frameWindowRef.current;
      if (!currentFrameWindow) {
        return;
      }

      const message: CallLegacyMessage = {type: messageTypeProxy, method, args};
      currentFrameWindow.postMessage(message, origin);
    },
    [origin],
  );

  const sendIframeBoot = useCallback(
    async (rawBoot: unknown) => {
      callIframeMethod('boot', rawBoot);

      if (isBooted) {
        // If we've already booted, this is just a partial boot update (from pusher).
        return;
      }

      // We need to fetch a delta boot to synchronize changes made since the original boot was fetched.
      try {
        const deltaRawBoot = await fetchLegacyFrameDeltaBootAsync(rawBoot, companyUrl, buildCancelToken());
        callIframeMethod('boot', deltaRawBoot);
      } catch (error) {
        if (error instanceof CancelError || error instanceof HttpNetworkError) {
          return;
        }

        legacyFrameLogger.error('Unable to fetch delta boot for legacy frame', {
          error_message: error?.message,
        });
      }

      // Update the UI to show the legacy app.
      setIsBooted(true);
    },
    [callIframeMethod, isBooted, companyUrl, buildCancelToken],
  );

  const navigateLegacyAppToUrl = useCallback((url: string): void => {
    const frameWindow = frameWindowRef.current;
    if (!frameWindow) {
      return;
    }

    // To help guard against an infinite loop/recursion.
    isNavigatingLegacyAppRef.current = true;
    try {
      frameWindow.frontLegacyApp.navigateToUrl(url);
    } finally {
      isNavigatingLegacyAppRef.current = false;
    }
  }, []);

  /*
   * Event handlers.
   */

  const onRawBoot: (arg: unknown) => void = useCallback(
    (rawBoot) => {
      // If the frame is loaded, just pass it on.
      if (isLoadedRef.current) {
        sendIframeBoot(rawBoot);
      }
    },
    [sendIframeBoot],
  );

  const onFrameLoaded = useCallback(
    async ({source}: MessageEvent) => {
      if (!source) {
        return;
      }

      const frameWindow = source as LegacyAppWindow;
      frameWindowRef.current = frameWindow;
      isLoadedRef.current = true;

      const rawBoot = await fetchRawBootMaybeIncrementallyAsync(async () => undefined);
      sendIframeBoot(rawBoot);

      const {frontLegacyApp} = frameWindow;

      frontLegacyApp.hideSettingsChrome();
      frontLegacyApp.hideDuplicateElementsFromP0();

      frontLegacyApp.registerRewriteDelegate({
        navigateToUrl: (url) => {
          // Ignore Old Front's no-op URL.
          if (url === '/noop') {
            return false;
          }

          // Guard against an infinite loop/recursion.
          const isNavigatingLegacyApp = isNavigatingLegacyAppRef.current;
          if (isNavigatingLegacyApp) {
            return false;
          }

          // Handle the URL. At a minimum, this will update our window history.
          dispatch(navigateLegacyFrameAction(panelId, url));

          // Let Old Front handle the URL if it's a "legacy" path and it hasn't been migrated to New Front.
          const isLegacyAppUrl = isLegacyPath(url) && !findNewLegacyRoute(url);
          return !isLegacyAppUrl;
        },
        setHasOverlay,
      });
    },
    [panelId, sendIframeBoot, dispatch, findNewLegacyRoute],
  );

  const onFrameMessage = useCallback(
    async (event: MessageEvent) => {
      // Validate the origin of the event/
      if (event.origin !== origin || !event.data || event.data.type !== messageTypeProxy) {
        return;
      }

      const {method: methodName, args: methodArguments} = event.data;
      if (!isString(methodName) || !isArray(methodArguments)) {
        return;
      }

      // Parse our input to ensure correct types.
      const rawLegacyFrameMethod = {[methodName]: methodArguments.length > 0 ? methodArguments : undefined};
      const legacyFrameMethod = readApiModel(ApiLegacyFrameMethods, rawLegacyFrameMethod);

      // Handle the initial "loaded" event.
      if (methodName === LegacyFrameMethodsEnum.LOADED) {
        onFrameLoaded(event);
        return;
      }

      // For any other proxy call, we need to be loaded first.
      if (!isVisible || !isLoadedRef.current || !isLegacyFrameMethodName(methodName, legacyFrameMethod)) {
        return;
      }

      const method = legacyFrameMethods[methodName];
      if (!method) {
        throw new ErrorWithData('Legacy frame method not found.', {methodName, methodArguments});
      }

      // Run the method and get the action, if any.
      const methodAction = method(...((legacyFrameMethod[methodName] as any) || []));
      if (!methodAction) {
        return;
      }

      // If one was found, dispatch it.
      const methodActionResult = dispatch(methodAction);

      // If this is not a promise, stop here.
      if (!isPromise(methodActionResult)) {
        return;
      }

      // Otherwise, wait for the promise to complete.
      await methodActionResult;
    },
    [origin, isVisible, dispatch, onFrameLoaded],
  );

  const maybeExitSettings = useCallback((onDiscard: () => void) => {
    const frameWindow = frameWindowRef.current;
    if (!frameWindow) {
      onDiscard();
      return;
    }

    frameWindow.frontLegacyApp.maybeExitSettings(onDiscard);
  }, []);

  /*
   * Lifecycle.
   */

  // Send the boot.
  useEffect(() => {
    const subscription = rawBoot$.pipe(toRawBootForLegacyFrame(companyUrl)).subscribe(onRawBoot);
    if (initialRawBoot && initialRawBoot.hasValue()) {
      initialRawBoot.withValue((rawBoot) => rawBoot$.next({isInitial: true, rawBoot}));
    }
    return () => subscription.unsubscribe();
  }, [rawBoot$, onRawBoot, companyUrl, initialRawBoot]);

  // Send location.
  useLayoutEffect(() => {
    if (navigationTimingRef.current?.path !== oldLegacyPath) {
      navigationTimingRef.current = {
        path: oldLegacyPath,
        start: performance.now(),
      };
    }

    if (!isBooted) {
      return;
    }

    try {
      navigateLegacyAppToUrl(oldLegacyPath);

      const timing = navigationTimingRef.current;
      if (timing?.path === oldLegacyPath && oldLegacyPath !== '/noop') {
        legacyFrameLogger.info('Legacy frame navigated', {
          path: oldLegacyPath,
          duration: performance.now() - timing.start,
          has_lazy_legacy_frame: true,
        });
      }
    } catch (error) {
      legacyFrameLogger.error('Unable to navigate to legacy url.', {
        path: oldLegacyPath,
        message: `${error?.message}`,
      });
    }
  }, [isBooted, navigateLegacyAppToUrl, oldLegacyPath]);

  // Send pusher events.
  usePushEvents(
    (eventName, eventData) => {
      if (!isLoadedRef.current || !shouldRelayPushEventToLegacyFrame(eventName, eventData)) {
        return;
      }

      callIframeMethod('dispatchPushEvent', eventName, eventData);
    },
    [callIframeMethod],
  );

  useEffect(() => {
    annotateLegacyFrameVisibilityChange(isVisible);
  }, [isVisible]);

  useEffect(() => {
    if (!isBooted) {
      return;
    }

    const window = frameWindowRef.current;
    if (!window) {
      throw new Error('A ref could not be found for this plugin iframe.');
    }

    const payload = {type: messageTypeProxy, method: 'set_partial_render', value: isPartialRender};
    window.postMessage(payload, origin);
  }, [isBooted, isPartialRender, origin]);

  /*
   * Render.
   */

  const SettingsAppFrame = hasSettingsNavigationV2 ? StyledAppFrameV2 : StyledAppFrame;

  return (
    <SettingsAppFrame isVisible={isVisible}>
      <SettingsNavigationProvider matchProps={newLegacyRoute?.match}>
        {!isUndefined(settingsPath) && (
          <ExpandedDarkThemeProvider>
            <DarkSidebarThemeProvider>
              <SettingsNavigation
                hasSettingsNavigationV2={hasSettingsNavigationV2}
                hasOverlay={Boolean(hasOverlay && !newLegacyRoute)}
                settingsPath={settingsPath}
                maybeExitSettings={maybeExitSettings}
              />
            </DarkSidebarThemeProvider>
          </ExpandedDarkThemeProvider>
        )}
        <StyledMainDiv>
          <SettingsBannersProvider>
            {!isUndefined(settingsPath) && <SettingsBannersHost />}
            {newLegacyRoute?.route.render(settingsPanelId, newLegacyRoute.match)}
            {hasGoneOnlineRef.current && hasNavigatedToOldFrontRef.current ? (
              <Iframe
                endpoint="/?serve=legacy"
                title="Settings"
                isVisible={(!newLegacyRoute || isPartialRender) && isBooted}
                onMessage={onFrameMessage}
              />
            ) : null}
            {(!newLegacyRoute || isPartialRender) && !isBooted && preBootElement}
          </SettingsBannersProvider>
        </StyledMainDiv>
      </SettingsNavigationProvider>
    </SettingsAppFrame>
  );
});

/*
 * Helpers.
 */

const previewMessage = (
  <Trans id="preview-iframe-unavailable">Old Front is not available in deploy previews.</Trans>
);

const preBootElement = <StyledCenteredDiv>{isPreview() ? previewMessage : <Loader />}</StyledCenteredDiv>;

function isLegacyFrameMethodName(
  methodName: string,
  legacyFrameMethod: LegacyFrameMethodsModel,
): methodName is keyof LegacyFrameMethodsModel {
  return methodName in legacyFrameMethod;
}

/*
 * Legacy frame method mapping.
 */

type LegacyFrameMethodReturnValue = void | ThunkAction<unknown> | ThunkAsyncAction<unknown>;
type LegacyFrameMethod<T extends keyof LegacyFrameMethodsModel, M extends LegacyFrameMethodsModel[T]> =
  M extends Array<any> ? (...args: M) => LegacyFrameMethodReturnValue : () => LegacyFrameMethodReturnValue;
type LegacyFrameMethods = {
  [T in keyof LegacyFrameMethodsModel]?: LegacyFrameMethod<T, LegacyFrameMethodsModel[T]>;
};

const legacyFrameMethods: LegacyFrameMethods = {
  [LegacyFrameMethodsEnum.COMPOSE]: composeLegacyFrameAction,
  [LegacyFrameMethodsEnum.NAVIGATE]: noop,
  [LegacyFrameMethodsEnum.OPEN_URL]: openUrlLegacyFrameAction,
  [LegacyFrameMethodsEnum.RESET_OFFLINE_CACHE]: resetOfflineCacheLegacyFrameAction,
};
